import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  updateDoc,
  deleteDoc,
  Timestamp,
  onSnapshot,
  QuerySnapshot
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { Provider, ProviderType, ProviderStats } from '@/models/provider.model';
import { auditListQuery } from '@/utils/audit-integration';
import { AuditEntityType } from '@/models/audit-log.model';

const PROVIDERS_COLLECTION = 'providers';
const ADMIN_PROVIDERS_INDEX_COLLECTION = 'admin_index_providers';

export class ProviderService {
  /**
   * Get all providers
   */
  static async getAllProviders(): Promise<Provider[]> {
    const q = query(
      collection(db, PROVIDERS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    const providers = snapshot.docs.map(doc => this.docToProvider(doc.id, doc.data()));

    auditListQuery(AuditEntityType.provider, {
      queryName: 'all_providers',
      resultCount: providers.length,
    }).catch(console.error);

    return providers;
  }

  /**
   * Get provider by ID
   */
  static async getProviderById(id: string): Promise<Provider | null> {
    const docRef = doc(db, PROVIDERS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return null;
    }
    return this.docToProvider(docSnap.id, docSnap.data());
  }

  /**
   * Subscribe to providers changes
   */
  static subscribeToProviders(
    callback: (providers: Provider[]) => void
  ): () => void {
    const q = query(
      collection(db, PROVIDERS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    
    return onSnapshot(q, (snapshot: QuerySnapshot) => {
      const providers = snapshot.docs.map(doc => 
        this.docToProvider(doc.id, doc.data())
      );
      callback(providers);
    });
  }

  /**
   * Search providers by name, city, or specialty
   */
  static async searchProviders(searchTerm: string): Promise<Provider[]> {
    const allProviders = await this.getAllProviders();
    const lowerSearch = searchTerm.toLowerCase();
    
    const filtered = allProviders.filter(provider => 
      provider.name.toLowerCase().includes(lowerSearch) ||
      provider.city?.toLowerCase().includes(lowerSearch) ||
      provider.state?.toLowerCase().includes(lowerSearch) ||
      provider.specialties.some(s => s.toLowerCase().includes(lowerSearch))
    );

    auditListQuery(AuditEntityType.provider, {
      queryName: 'search_providers',
      resultCount: filtered.length,
      filters: { searchTerm },
    }).catch(console.error);

    return filtered;
  }

  /**
   * Filter providers by type (uses admin index for performance)
   */
  static async filterProvidersByType(type: ProviderType): Promise<Provider[]> {
    // Use admin index for efficient querying
    const q = query(
      collection(db, ADMIN_PROVIDERS_INDEX_COLLECTION),
      where('type', '==', type),
      orderBy('isFairCarePreferred', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const providers: Provider[] = [];
    
    // Fetch full provider documents from index references
    for (const docSnap of snapshot.docs) {
      const indexData = docSnap.data();
      const providerId = indexData.providerId || docSnap.id;
      
      try {
        const providerDoc = await getDoc(doc(db, PROVIDERS_COLLECTION, providerId));
        if (providerDoc.exists()) {
          providers.push(this.docToProvider(providerDoc.id, providerDoc.data()));
        }
      } catch (error) {
        console.error(`Error fetching provider ${providerId}:`, error);
      }
    }

    // Sort by createdAt in memory (since we can't use multiple orderBy without composite index)
    providers.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

    auditListQuery(AuditEntityType.provider, {
      queryName: 'filter_providers_by_type',
      resultCount: providers.length,
      filters: { type },
    }).catch(console.error);

    return providers;
  }

  /**
   * Get provider statistics
   */
  static async getProviderStats(): Promise<ProviderStats> {
    const providers = await this.getAllProviders();
    
    return {
      totalProviders: providers.length,
      subscribedProviders: providers.filter(p => p.isSubscribed).length,
      preferredProviders: providers.filter(p => p.isFairCarePreferred).length,
      providerTypes: new Set(providers.map(p => p.type)).size,
    };
  }

  /**
   * Toggle provider subscription
   */
  static async toggleSubscription(providerId: string, isSubscribed: boolean): Promise<void> {
    const docRef = doc(db, PROVIDERS_COLLECTION, providerId);
    await updateDoc(docRef, {
      isSubscribed: !isSubscribed,
      lastUpdated: Timestamp.now(),
    });
  }

  /**
   * Toggle provider preferred status
   */
  static async togglePreferred(providerId: string, isPreferred: boolean): Promise<void> {
    const docRef = doc(db, PROVIDERS_COLLECTION, providerId);
    await updateDoc(docRef, {
      isFairCarePreferred: !isPreferred,
      lastUpdated: Timestamp.now(),
    });
  }

  /**
   * Update provider
   */
  static async updateProvider(providerId: string, updates: Partial<Provider>): Promise<void> {
    const docRef = doc(db, PROVIDERS_COLLECTION, providerId);
    const updateData: any = {
      ...updates,
      lastUpdated: Timestamp.now(),
    };
    
    // Convert Date objects to Timestamps
    if (updates.subscriptionExpiresAt) {
      updateData.subscriptionExpiresAt = Timestamp.fromDate(updates.subscriptionExpiresAt);
    }
    
    // Remove id from updates
    delete updateData.id;
    
    await updateDoc(docRef, updateData);
  }

  /**
   * Delete provider
   */
  static async deleteProvider(providerId: string): Promise<void> {
    const docRef = doc(db, PROVIDERS_COLLECTION, providerId);
    await deleteDoc(docRef);
  }

  /**
   * Convert Firestore document to Provider
   */
  private static docToProvider(id: string, data: any): Provider {
    return {
      id,
      userId: data.userId,
      email: data.email,
      name: data.name || '',
      type: data.type ?? ProviderType.other,
      address: data.address,
      city: data.city,
      state: data.state,
      zipCode: data.zipCode,
      latitude: data.latitude,
      longitude: data.longitude,
      phoneNumber: data.phoneNumber,
      website: data.website,
      averageCashPrice: data.averageCashPrice,
      servicePrices: data.servicePrices,
      rating: data.rating,
      totalReviews: data.totalReviews,
      isFairCarePreferred: data.isFairCarePreferred ?? false,
      acceptsCash: data.acceptsCash ?? true,
      isSubscribed: data.isSubscribed ?? false,
      stripeAccountId: data.stripeAccountId,
      subscriptionTier: data.subscriptionTier,
      subscriptionExpiresAt: data.subscriptionExpiresAt?.toDate(),
      specialties: data.specialties || [],
      metadata: data.metadata,
      createdAt: data.createdAt?.toDate() || new Date(),
      lastUpdated: data.lastUpdated?.toDate() || new Date(),
    };
  }
}

